import { useState, useEffect } from 'react';
import { getStreetFoodList } from '../lib/api';
import { LocalizedString, ExtendedSection } from './extended-sections';

export type VendorType =
'cart' |
'hybrid-shop' |
'hole-in-wall' |
'sidewalk-stand' |
'street-counter';

export interface StreetFoodDish {
  name: LocalizedString;
  price?: string;
  description?: LocalizedString;
  isSpicy?: boolean;
  isMustTry?: boolean;
  image?: string;
}

export interface StreetFoodVendor {
  id: string;
  name: LocalizedString;
  tagline: LocalizedString;
  vendorType: VendorType;
  neighborhood: LocalizedString;
  neighborhoodSlug: string;
  locationDescription: LocalizedString;
  address?: LocalizedString;
  location: {lat: number;lng: number;};
  operatingHours: LocalizedString;
  bestTime: LocalizedString;
  ownerName?: LocalizedString;
  sinceYear?: number;
  signatureDish: LocalizedString;
  priceRange: string;
  seatingStyle: string;
  crowdLevel: string;
  spiceLevel?: string;
  paymentInfo?: {
    cashOnly?: boolean;
    notes?: LocalizedString;
  };
  dishes: StreetFoodDish[];
  story?: LocalizedString;
  ritual?: any;
  storyExcerpt?: LocalizedString;
  tips?: LocalizedString[];
  tags?: string[];
  images: string[];
  featuredOnHomepage?: boolean;
  isEditorsChoice?: boolean;
  extendedSections?: ExtendedSection[];
  brandLogo?: string;
}

const VENDOR_TYPES: VendorType[] = [
  'cart',
  'hybrid-shop',
  'hole-in-wall',
  'sidewalk-stand',
  'street-counter',
];

function asLocalized(v: unknown): LocalizedString {
  if (v && typeof v === 'object' && 'en' in v && 'ar' in v) {
    const o = v as { en?: unknown; ar?: unknown };
    return { en: String(o.en ?? ''), ar: String(o.ar ?? '') };
  }
  if (typeof v === 'string') return { en: v, ar: v };
  return { en: '', ar: '' };
}

function normalizeVendorType(v: unknown): VendorType {
  const s = String(v || 'street-counter');
  return VENDOR_TYPES.includes(s as VendorType) ?
    (s as VendorType) :
    'street-counter';
}

/** Maps GET /api/street-food row into `StreetFoodVendor`. */
export function streetFoodVendorFromApi(
  row: Record<string, unknown>,
): StreetFoodVendor {
  const dishesRaw = row.dishes;
  const dishes: StreetFoodDish[] = Array.isArray(dishesRaw) ?
    dishesRaw.map((d) => {
      const o = d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
      return {
        name: asLocalized(o.name),
        price: o.price != null ? String(o.price) : undefined,
        description: o.description ? asLocalized(o.description) : undefined,
        isSpicy: o.isSpicy === true,
        isMustTry: o.isMustTry === true,
        image: o.image != null ? String(o.image) : undefined,
      };
    }) :
    [];

  const imgsRaw = row.images;
  const images: string[] = Array.isArray(imgsRaw) ?
    imgsRaw.map((u) => String(u)).filter(Boolean) :
    [];
  const cover = row.coverImage != null ? String(row.coverImage) : '';
  const imageList =
    images.length > 0 ? images : cover ? [cover] : [];

  const payment = row.paymentInfo;
  const paymentInfo =
    payment && typeof payment === 'object' ?
      (payment as StreetFoodVendor['paymentInfo']) :
      { cashOnly: true };

  const loc = row.location;
  let lat = 31.2;
  let lng = 29.9;
  if (loc && typeof loc === 'object') {
    const c = loc as { lat?: unknown; lng?: unknown };
    lat = Number(c.lat ?? lat);
    lng = Number(c.lng ?? lng);
  }

  const ext = row.extendedSections;
  const extendedSections = Array.isArray(ext) ?
    (ext as ExtendedSection[]) :
    undefined;

  const id = String(row.id ?? '');
  const story = row.story ? asLocalized(row.story) : { en: '', ar: '' };

  return {
    id,
    name: asLocalized(row.name),
    tagline: asLocalized(row.tagline),
    vendorType: normalizeVendorType(row.vendorType),
    neighborhood: asLocalized(row.neighborhood),
    neighborhoodSlug: String(row.neighborhoodSlug ?? ''),
    locationDescription: asLocalized(row.locationDescription),
    address: row.address ? asLocalized(row.address) : undefined,
    location: { lat, lng },
    operatingHours: asLocalized(row.operatingHours),
    bestTime: asLocalized(row.bestTime),
    ownerName: row.ownerName ? asLocalized(row.ownerName) : undefined,
    sinceYear:
      row.sinceYear != null ? Number(row.sinceYear) : undefined,
    signatureDish: asLocalized(row.signatureDish),
    priceRange: String(row.priceRange ?? ''),
    seatingStyle: String(row.seatingStyle ?? ''),
    crowdLevel: String(row.crowdLevel ?? ''),
    spiceLevel:
      row.spiceLevel != null ? String(row.spiceLevel) : undefined,
    paymentInfo,
    dishes,
    story,
    ritual: row.ritual as StreetFoodVendor['ritual'],
    storyExcerpt: row.storyExcerpt ?
      asLocalized(row.storyExcerpt) :
      undefined,
    tips: Array.isArray(row.tips) ?
      (row.tips as LocalizedString[]) :
      undefined,
    tags: Array.isArray(row.tags) ?
      (row.tags as string[]) :
      [],
    images: imageList,
    featuredOnHomepage: row.featuredOnHomepage === true,
    isEditorsChoice: row.isEditorsChoice === true,
    extendedSections,
    brandLogo:
      row.brandLogo != null ? String(row.brandLogo) : undefined,
  };
}

export const STREET_FOOD_VENDORS: StreetFoodVendor[] = [
{
  id: 'awlad-abdo-attareen',
  name: { en: 'Awlad Abdo', ar: 'أولاد عبده' },
  tagline: {
    en: 'The chaotic symphony of Alexandrian street sandwiches',
    ar: 'سيمفونية الفوضى اللذيذة لساندوتشات الإسكندرية'
  },
  vendorType: 'street-counter',
  neighborhood: { en: 'Attareen', ar: 'العطارين' },
  neighborhoodSlug: 'attareen',
  locationDescription: {
    en: 'In the heart of the antique market district',
    ar: 'في قلب منطقة سوق الأنتيكات'
  },
  location: { lat: 31.195, lng: 29.905 },
  operatingHours: {
    en: 'Early morning to late night',
    ar: 'من الصباح الباكر حتى وقت متأخر من الليل'
  },
  bestTime: {
    en: 'Late night for the full chaotic experience',
    ar: 'بالليل متأخر علشان تعيش تجربة الزحمة كاملة'
  },
  signatureDish: {
    en: 'Pastrami & Egg Sandwich',
    ar: 'ساندوتش بيض بالبسطرمة'
  },
  priceRange: '$$',
  seatingStyle: 'standing',
  crowdLevel: 'always-busy',
  paymentInfo: {
    cashOnly: true,
    notes: {
      en: 'Pay the boss at the dessert counter when you finish',
      ar: 'الحساب عند المعلم في قسم الحلويات بعد ما تخلص أكل'
    }
  },
  dishes: [
  { name: { en: 'Pastrami & Egg', ar: 'بيض بالبسطرمة' }, isMustTry: true },
  { name: { en: 'Roast Beef', ar: 'روزبيف' } },
  { name: { en: 'Fried Shrimp', ar: 'جمبري مقلي' } },
  { name: { en: 'Meat with Onions', ar: 'لحمة بالبصل' } },
  { name: { en: 'Cream & Honey', ar: 'قشطة بالعسل' } }],

  storyExcerpt: {
    en: 'Originally a dairy shop famous for its house-made pastrami, Awlad Abdo evolved into a legendary street food experience where you shout your order over the crowd.',
    ar: 'في الأصل كان محل ألبان مشهور بالبسطرمة البيتي، وتطور أولاد عبده ليصبح تجربة أكل شارع أسطورية بتنادي فيها بطلبك وسط الزحمة.'
  },
  story: {
    en: "Awlad Abdo is not just a place to eat; it is a quintessential Alexandrian experience. Originally a humble dairy shop selling milk, cheese, and their famous house-made pastrami, it organically transformed into one of the city's most beloved street food counters. \n\nThe magic of Awlad Abdo lies in its organized chaos. There are no menus, no waiters, and no tables. You stand on the street, surrounded by the antique shops of Attareen, and participate in a fast-paced culinary ritual. The sandwich makers operate at lightning speed, assembling fresh ingredients into soft fino bread while simultaneously memorizing dozens of shouted orders from the surrounding crowd. \n\nIt is a place where locals from all walks of life stand shoulder-to-shoulder, grabbing pickles from open jars, pulling sodas from the fridge, and enjoying the freshest, most authentic sandwiches in the city.",
    ar: 'أولاد عبده مش مجرد مكان تاكل فيه؛ ده تجربة إسكندرانية أصيلة. في الأصل كان محل ألبان بسيط بيبيع لبن وجبنة وبسطرمة بيتي مشهور بيها جداً، وتطور بشكل طبيعي علشان يبقى واحد من أشهر محلات الساندوتشات في الشارع السكندري.\n\nسحر أولاد عبده في الفوضى المنظمة بتاعته. مفيش منيو، مفيش جرسونات، ومفيش ترابيزات. إنت بتقف في الشارع، وسط محلات الأنتيكات في العطارين، وبتشارك في طقس أكل سريع جداً. الصنايعية بيعملوا الساندوتشات بسرعة البرق، بيحطوا المكونات الطازة في عيش فينو طري، وفي نفس الوقت حافظين عشرات الطلبات اللي الناس بتنادي بيها وسط الزحمة.\n\nده مكان بيقف فيه ولاد البلد من كل الطبقات كتف بكتف، بياخدوا مخلل من البرطمانات المفتوحة، بيسحبوا حاجة ساقعة من التلاجة، وبيستمتعوا بأطعم وأطزج ساندوتشات في المدينة.'
  },
  ritual: {
    intro: {
      en: 'Eating at Awlad Abdo requires knowing the unwritten rules. Here is how you navigate the delicious chaos like a true local:',
      ar: 'الأكل عند أولاد عبده محتاج تكون عارف القواعد غير المكتوبة. دي الطريقة اللي تاكل بيها وسط الزحمة اللذيذة زي ولاد البلد:'
    },
    steps: [
    {
      title: { en: 'Shout Your Order', ar: 'نادي بطلبك' },
      description: {
        en: 'Push through the crowd to the counter and shout your order to the sandwich makers (e.g., "3 eggs with pastrami, 2 roast beef!").',
        ar: 'ادخل وسط الزحمة لحد الكاونتر ونادي بطلبك للصنايعية بصوت عالي (مثلاً: "٣ بيض بالبسطرمة و٢ روزبيف!").'
      }
    },
    {
      title: { en: 'Eat Standing Up', ar: 'كل وأنت واقف' },
      description: {
        en: 'Grab your sandwiches as they are handed to you. Stand on the sidewalk, grab some pickles from the open jars, and get drinks from the fridge.',
        ar: 'استلم ساندوتشاتك أول ما تخلص. اقف على الرصيف، خد مخلل من البرطمانات المفتوحة قدامك، وهات شربك من التلاجة.'
      }
    },
    {
      title: { en: 'Keep Ordering & Memorize', ar: 'اطلب تاني وافتكر' },
      description: {
        en: 'Want more? Just shout again. But you MUST memorize exactly what you ate and drank—there are no written checks.',
        ar: 'عاوز تاني؟ نادي تاني عادي. بس لازم تفتكر بالظبط إنت أكلت وشربت إيه لأن مفيش شيكات مكتوبة.'
      }
    },
    {
      title: { en: 'Pay the Boss', ar: 'حاسب المعلم' },
      description: {
        en: 'When full, go to the owner standing by the sweet sandwiches (qishta and honey). Recite your list. He will calculate your total instantly in his head.',
        ar: 'لما تشبع، روح لصاحب المحل اللي واقف عند قسم الساندوتشات الحلوة (القشطة والعسل). قوله إنت أكلت إيه، وهو هيحسب الحساب في دماغه فوراً.'
      }
    }]

  },
  tags: ['historic', 'crowded', 'standing-only', 'fast-paced', 'sandwiches'],
  images: [
  'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1200&q=80',
  'https://images.unsplash.com/photo-1528736235302-52922df5c122?w=1200&q=80']

},
{
  id: 'youssef-ashour-karmouz',
  name: { en: 'Youssef Ashour', ar: 'يوسف عاشور' },
  tagline: {
    en: 'Butcher by day, legendary kebab joint by night',
    ar: 'جزار بالنهار، وحاتي أسطوري بالليل'
  },
  vendorType: 'hybrid-shop',
  neighborhood: { en: 'Karmouz', ar: 'كرموز' },
  neighborhoodSlug: 'karmouz',
  locationDescription: {
    en: 'Deep in the historic Karmouz district',
    ar: 'في عمق حي كرموز التاريخي'
  },
  location: { lat: 31.188, lng: 29.895 },
  operatingHours: { en: '9:00 PM to Dawn', ar: 'من ٩ مساءً حتى الفجر' },
  bestTime: { en: 'Midnight', ar: 'منتصف الليل' },
  ownerName: { en: 'Youssef Ashour', ar: 'يوسف عاشور' },
  signatureDish: { en: 'Double-Knife Hawawshi', ar: 'حواوشي لحمة سكينتين' },
  priceRange: '$$',
  seatingStyle: 'sidewalk-tables',
  crowdLevel: 'busy-at-peak',
  paymentInfo: { cashOnly: true },
  dishes: [
  {
    name: { en: 'Double-Knife Hawawshi', ar: 'حواوشي لحمة سكينتين' },
    isMustTry: true,
    description: {
      en: 'Meat chopped with two knives, not minced',
      ar: 'لحمة متقطعة بسكينتين مش مفرومة'
    }
  },
  { name: { en: 'Kebab & Kofta', ar: 'كباب وكفتة' } },
  { name: { en: 'Tarb', ar: 'طرب' } }],

  storyExcerpt: {
    en: 'A true hidden gem that transforms as the sun sets. During the day, it is a standard butcher shop preparing fresh meat. At 9 PM, the grills fire up.',
    ar: 'جوهرة خفية حقيقية تتحول مع غروب الشمس. بالنهار هو محل جزارة عادي يجهز اللحمة الطازجة. الساعة ٩ بالليل، بتولع الشوايات.'
  },
  story: {
    en: 'Youssef Ashour represents the ultimate "insider secret" of Alexandria. Located deep in the historic, working-class neighborhood of Karmouz, this establishment leads a double life. By day, it operates as a traditional neighborhood butcher shop, receiving and preparing the freshest cuts of meat. \n\nBut as the clock strikes 9:00 PM, a remarkable transformation occurs. The butcher blocks are cleared, the massive charcoal grills are fired up, and the shop turns into a bustling, open-air kebab joint. Tables and plastic chairs spill out onto the sidewalk and into adjacent rented storefronts to accommodate the nightly crowds. \n\nThe absolute must-try here is the "Double-Knife Hawawshi" (Hawawshi Lahma Sekkentein). Unlike standard hawawshi made with ground meat, Ashour\'s version uses premium cuts of meat meticulously hand-chopped using two large butcher knives. This technique preserves the texture and juices of the meat, creating a legendary dish that draws food lovers from across the city until the early hours of the morning.',
    ar: 'يوسف عاشور بيمثل "السر الأكبر" لأولاد البلد في الإسكندرية. مكانه في عمق حي كرموز الشعبي التاريخي، والمكان ده بيعيش حياة مزدوجة. بالنهار، بيشتغل كمحل جزارة تقليدي بيستقبل ويجهز أطزج قطع اللحمة.\n\nلكن أول ما تيجي الساعة ٩ بالليل، بيحصل تحول مذهل. قرمة الجزارة بتتشال، شوايات الفحم الضخمة بتولع، والمحل بيتحول لمطعم كباب وحاتي مفتوح في الشارع. الترابيزات والكراسي البلاستيك بتفرش على الرصيف وفي المحلات الصغيرة المجاورة اللي مأجرها علشان تستوعب زحمة الليل.\n\nالطلب اللي لازم تجربه هنا هو "حواوشي لحمة سكينتين". على عكس الحواوشي العادي اللي بيتعمل بلحمة مفرومة، حواوشي عاشور بيتعمل بقطع لحمة ممتازة بتتقطع يدوياً بعناية باستخدام سكينتين جزارة كبار. الطريقة دي بتحافظ على قوام وعصارة اللحمة، وبتخلق طبق أسطوري بيجذب عشاق الأكل من كل أنحاء المدينة لحد الساعات الأولى من الفجر.'
  },
  tags: ['late-night', 'meat-lovers', 'hybrid', 'sidewalk-seating'],
  images: [
  'https://images.unsplash.com/photo-1544025162-d76694265947?w=1200&q=80',
  'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&q=80']

},
{
  id: 'awlad-al-fallah-raml',
  name: { en: 'Awlad Al-Fallah', ar: 'أولاد الفلاح' },
  tagline: {
    en: 'The undisputed king of Alexandrian Liver',
    ar: 'ملك الكبدة الإسكندراني بلا منازع'
  },
  vendorType: 'hole-in-wall',
  neighborhood: { en: 'Mahatet El-Raml', ar: 'محطة الرمل' },
  neighborhoodSlug: 'mahatet-el-raml',
  locationDescription: {
    en: 'Near the tram station',
    ar: 'بالقرب من محطة الترام'
  },
  location: { lat: 31.2, lng: 29.9 },
  operatingHours: { en: '24/7', ar: 'مفتوح ٢٤ ساعة' },
  bestTime: {
    en: 'Anytime, but late night has a special vibe',
    ar: 'في أي وقت، بس نص الليل ليه جو خاص'
  },
  signatureDish: { en: 'Spicy Alexandrian Liver', ar: 'كبدة إسكندراني حارة' },
  priceRange: '$',
  seatingStyle: 'mixed',
  crowdLevel: 'always-busy',
  spiceLevel: 'spicy',
  paymentInfo: { cashOnly: true },
  dishes: [
  {
    name: { en: 'Liver Sandwich', ar: 'ساندوتش كبدة' },
    isMustTry: true,
    isSpicy: true
  },
  { name: { en: 'Sausage Sandwich', ar: 'ساندوتش سجق' } }],

  storyExcerpt: {
    en: 'People travel from all over Egypt just to eat here. The secret spice blend and the sheer volume of liver cooked daily make it an unmatched experience.',
    ar: 'الناس بتسافر من كل أنحاء مصر مخصوص علشان تاكل هنا. خلطة التوابل السرية وكمية الكبدة اللي بتتعمل كل يوم بتخليه تجربة ملهاش مثيل.'
  },
  story: {
    en: 'If there is one dish Alexandria is famous for across the Middle East, it is "Kebda Eskandarani" (Alexandrian Liver)—and Awlad Al-Fallah is its undisputed king. Located near the bustling Mahatet El-Raml tram station, this unassuming hole-in-the-wall operates 24 hours a day, 7 days a week, and is almost never empty. \n\nWhat makes Awlad Al-Fallah unique is their closely guarded secret spice blend and the massive, continuous volume of cooking. The liver is sliced paper-thin, tossed with fiery green peppers, garlic, and spices, and flash-fried on massive flat-top griddles before being stuffed into fresh fino bread. \n\nThe experience is visceral. You squeeze through the crowds, order your sandwiches (locals usually order them by the half-dozen), and eat standing on the sidewalk while the spicy juices drip. They have recently acquired adjacent shops to provide some seating, but eating on your feet amid the chaos remains the truest way to experience it.',
    ar: 'لو فيه أكلة واحدة الإسكندرية مشهورة بيها في الشرق الأوسط كله، فهي "الكبدة الإسكندراني" — وأولاد الفلاح هو ملك الكبدة بلا منازع. مكانه قريب من محطة الرمل المزدحمة، المحل البسيط ده شغال ٢٤ ساعة في اليوم، ٧ أيام في الأسبوع، وتقريباً مبيفضاش أبداً.\n\nاللي بيميز أولاد الفلاح هو خلطة التوابل السرية اللي محدش يعرفها، وحجم الشغل المستمر. الكبدة بتتقطع شرايح رفيعة جداً، وتتقلب مع فلفل أخضر حامي وتوم وتوابل، وتتشوح بسرعة على شوايات مسطحة ضخمة قبل ما تتحشى في عيش فينو طازة.\n\nالتجربة هناك حية جداً. بتدخل وسط الزحمة، تطلب ساندوتشاتك (ولاد البلد بيطلبوها بالأنصاص)، وتاكل وأنت واقف على الرصيف وعصارة الكبدة بتنقط. مؤخراً أجروا محلات جنبهم علشان يوفروا أماكن للجلوس، بس الأكل وأنت واقف وسط الزحمة لسه هو أمتع طريقة تعيش بيها التجربة.'
  },
  tags: ['24-7', 'spicy', 'famous', 'standing-only', 'liver'],
  images: [
  'https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=1200&q=80',
  'https://images.unsplash.com/photo-1626804475297-41609ea0af49?w=1200&q=80']

},
{
  id: 'mansheya-spicy-ful',
  name: { en: 'Mansheya Courthouse Ful', ar: 'فول محكمة المنشية' },
  tagline: {
    en: 'The spiciest breakfast cart in the city',
    ar: 'أحمش عربية فول للفطار في المدينة'
  },
  vendorType: 'cart',
  neighborhood: { en: 'Mansheya', ar: 'المنشية' },
  neighborhoodSlug: 'mansheya',
  locationDescription: {
    en: 'Right next to the Mansheya Courthouse',
    ar: 'بجوار محكمة المنشية مباشرة'
  },
  location: { lat: 31.198, lng: 29.892 },
  operatingHours: {
    en: 'Early morning until pots are empty (usually 11 AM)',
    ar: 'من الصباح الباكر حتى انتهاء القدر (غالباً ١١ صباحاً)'
  },
  bestTime: { en: '7:00 AM', ar: '٧:٠٠ صباحاً' },
  signatureDish: {
    en: 'Spicy Ful with Secret Oil',
    ar: 'فول حامي بالزيت السري'
  },
  priceRange: '$',
  seatingStyle: 'standing',
  crowdLevel: 'busy-at-peak',
  spiceLevel: 'very-spicy',
  paymentInfo: { cashOnly: true },
  dishes: [
  {
    name: { en: 'Spicy Ful Plate', ar: 'طبق فول حامي' },
    isMustTry: true,
    isSpicy: true
  },
  { name: { en: 'Old Cheese with Tomato', ar: 'جبنة قديمة بالطماطم' } },
  { name: { en: 'Boiled Eggs', ar: 'بيض مسلوق' } }],

  storyExcerpt: {
    en: 'A simple wooden cart with three pots of slow-cooked fava beans. The owner is famous for making his ful exceptionally spicy.',
    ar: 'عربية خشب بسيطة عليها ٣ قدرات فول مدمس. صاحبها مشهور إنه بيعمل الفول "حامي" جداً.'
  },
  story: {
    en: 'In Egypt, the "Ful Cart" (Arabeyet Ful) is an institution, and every neighborhood claims to have the best one. But the cart stationed next to the Mansheya Courthouse holds a special, fiery reputation. \n\nIt is a classic, beautifully painted wooden cart holding three massive, narrow-necked pots (Qedras) where fava beans simmer overnight until they reach a buttery consistency. What sets this cart apart is the owner\'s heavy hand with spices and his signature chili oil. When you order your ful "hami" (spicy), be prepared for a serious kick that wakes you up faster than any coffee. \n\nEvery morning, a diverse crowd of suited lawyers heading to court, local merchants opening their shops, and brave foodies gather around the cart. You are handed a metal plate, a stack of fresh balady bread, and you eat standing up, dipping the bread into the rich, spicy stew while the city wakes up around you.',
    ar: 'في مصر، "عربية الفول" دي مؤسسة، وكل حي بيقول إن عنده أحسن عربية. بس العربية اللي واقفة جنب محكمة المنشية ليها سمعة خاصة وحراقة جداً.\n\nهي عربية خشب كلاسيكية متلونة بألوان زاهية، عليها ٣ قدرات ضخمة الفول بيدمس فيها طول الليل لحد ما يبقى زي الزبدة. اللي بيميز العربية دي هو إيد صاحبها السخية في التوابل وزيت الشطة المميز بتاعه. لما تطلب الفول "حامي"، استعد للسعة شطة هتفوقك أسرع من أي قهوة.\n\nكل يوم الصبح، بيتجمع حوالين العربية جمهور متنوع من المحامين ببدلهم وهما رايحين المحكمة، وتجار المنطقة اللي بيفتحوا محلاتهم، والأكيلة الشجعان. بتاخد طبق معدن، ورغيفين عيش بلدي طازة، وتاكل وأنت واقف، تغمس العيش في الفول الغني الحراق والمدينة بتصحى حواليك.'
  },
  tags: ['breakfast', 'spicy', 'cart', 'street-vibes'],
  images: [
  'https://images.unsplash.com/photo-1593011158564-9ee016258f3f?w=1200&q=80',
  'https://images.unsplash.com/photo-1540914124281-342587941389?w=1200&q=80']

}];


export const STORAGE_KEY = 'ahg.street_food.v1';
export const DELETED_KEY = 'ahg.street_food.deleted.v1';

export function loadAllVendors(): StreetFoodVendor[] {
  if (typeof window === 'undefined') return STREET_FOOD_VENDORS;

  try {
    const rawOverrides = window.localStorage.getItem(STORAGE_KEY);
    const rawDeleted = window.localStorage.getItem(DELETED_KEY);

    const overrides: Record<string, StreetFoodVendor> = rawOverrides ?
    JSON.parse(rawOverrides) :
    {};
    const deletedIds: string[] = rawDeleted ? JSON.parse(rawDeleted) : [];

    const merged = STREET_FOOD_VENDORS.map((v) => overrides[v.id] || v);

    const seedIds = new Set(STREET_FOOD_VENDORS.map((v) => v.id));
    Object.values(overrides).forEach((v) => {
      if (!seedIds.has(v.id)) {
        merged.push(v);
      }
    });

    return merged.filter((v) => !deletedIds.includes(v.id));
  } catch (e) {
    console.error('Failed to load street food vendors', e);
    return STREET_FOOD_VENDORS;
  }
}

export function loadVendor(id: string): StreetFoodVendor | undefined {
  return loadAllVendors().find((v) => v.id === id);
}

export function saveVendor(vendor: StreetFoodVendor) {
  if (typeof window === 'undefined') return;

  try {
    const rawOverrides = window.localStorage.getItem(STORAGE_KEY);
    const overrides: Record<string, StreetFoodVendor> = rawOverrides ?
    JSON.parse(rawOverrides) :
    {};

    overrides[vendor.id] = vendor;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));

    const rawDeleted = window.localStorage.getItem(DELETED_KEY);
    if (rawDeleted) {
      const deletedIds: string[] = JSON.parse(rawDeleted);
      const newDeleted = deletedIds.filter((id) => id !== vendor.id);
      window.localStorage.setItem(DELETED_KEY, JSON.stringify(newDeleted));
    }

    window.dispatchEvent(new Event('ahg.street_food.changed'));
  } catch (e) {
    console.error('Failed to save street food vendor', e);
  }
}

export function deleteVendor(id: string) {
  if (typeof window === 'undefined') return;

  try {
    const rawDeleted = window.localStorage.getItem(DELETED_KEY);
    const deletedIds: string[] = rawDeleted ? JSON.parse(rawDeleted) : [];

    if (!deletedIds.includes(id)) {
      deletedIds.push(id);
      window.localStorage.setItem(DELETED_KEY, JSON.stringify(deletedIds));
    }

    window.dispatchEvent(new Event('ahg.street_food.changed'));
  } catch (e) {
    console.error('Failed to delete street food vendor', e);
  }
}

export function useStreetFood() {
  const [vendors, setVendors] = useState<StreetFoodVendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const applyBundled = () => {
      setVendors(loadAllVendors());
    };

    (async () => {
      setIsLoading(true);
      try {
        const res = await getStreetFoodList({ limit: 200 });
        const mapped = res.data.map((row) =>
          streetFoodVendorFromApi(row as Record<string, unknown>),
        );
        if (!cancelled) setVendors(mapped);
      } catch {
        if (import.meta.env.DEV) {
          console.warn(
            '[api] street food fetch failed, using bundled fallback',
          );
        }
        if (!cancelled) applyBundled();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    const handleChanged = () => {
      applyBundled();
    };
    window.addEventListener('ahg.street_food.changed', handleChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('ahg.street_food.changed', handleChanged);
    };
  }, []);

  return {
    vendors,
    getVendor: (id: string) => vendors.find((v) => v.id === id),
    addVendor: saveVendor,
    updateVendor: (_id: string, v: StreetFoodVendor) => saveVendor(v),
    deleteVendor,
    isLoading,
  };
}